/**
 * What the server currently believes about the signed-in account.
 *
 * Every value here comes from `GET /auth/me` rather than from anything the
 * browser remembers, so it is the account's real state and not an optimistic
 * echo of the last action the user took.
 */
import type { ReactNode } from 'react'

import { ROLE_LABEL_KEYS } from '../api/types'
import type { MeResponse, RoleId } from '../api/types'
import { useI18n } from '../i18n'

export function AccountSummary({ user }: { user: MeResponse }) {
  const { t } = useI18n()
  // Roles as KEYS, not as text: a rendered role has to change with the
  // language, so the shared `domain.role.*` vocabulary is resolved at render
  // time rather than imported as an already-translated word.
  const roleKey = ROLE_LABEL_KEYS[user.role as RoleId]

  return (
    <section className="space-y-4 rounded-lg border border-border bg-surface p-6 shadow-sm">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-fg">{t('account.summary.title')}</h2>
        <p className="text-xs text-muted">{t('account.summary.note')}</p>
      </header>

      <dl className="grid gap-3 sm:grid-cols-3">
        <Row label={t('account.summary.email')}>
          <span className="num break-all text-fg">{user.email}</span>
        </Row>
        <Row label={t('account.summary.role')}>
          {/* An unrecognised role is shown verbatim rather than hidden: a role
              this build has never heard of is exactly what a reader needs to see. */}
          <span className="text-fg">{roleKey ? t(roleKey) : user.role}</span>
        </Row>
        <Row label={t('domain.twoFactor')} testId="summary-2fa">
          {user.totp_enabled ? (
            <span className="text-healthy">{t('account.summary.twoFactorEnabled')}</span>
          ) : (
            <span className="text-degraded">{t('account.summary.twoFactorDisabled')}</span>
          )}
        </Row>
      </dl>
    </section>
  )
}

function Row({
  label,
  testId,
  children,
}: {
  label: string
  testId?: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm font-medium" data-testid={testId}>
        {children}
      </dd>
    </div>
  )
}
